/**
 * Appointments Report Page
 *
 * Telehealth appointments operations dashboard.
 * Volume, no-show rates, provider utilization, source breakdown, lead times.
 *
 * Calendar days and hours of day are Eastern (America/New_York) — the timezone
 * TMates ops actually schedule in. No-show rate is always no-shows over
 * completed + no-shows; cancellations are a separate outcome.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { format, parse, subDays } from 'date-fns'
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, Cell,
} from 'recharts'
import { appointmentsAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { DateRangePicker } from '../components/DateRangePicker'
import DataFreshnessWarning from '../components/DataFreshnessWarning'
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
  Button
} from '../components/ui'
import {
  RefreshCw, Loader2, AlertCircle, Calendar, CheckCircle2, XCircle, Clock,
  TrendingUp, TrendingDown, Info, Download, ArrowUpDown, Ban, Video, Disc3, Smartphone
} from 'lucide-react'

function getDefaultDateRange() {
  const end = new Date()
  const start = subDays(end, 89)
  return { from: start, to: end }
}

function formatNumber(value) {
  if (value === null || value === undefined) return '-'
  return new Intl.NumberFormat('en-US').format(value)
}

function mondayOf(ymd) {
  const [y, m, d] = ymd.split('-').map(Number)
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay()
  return addDaysYmd(ymd, dow === 0 ? -6 : 1 - dow)
}

function addDaysYmd(ymd, n) {
  const [y, m, d] = ymd.split('-').map(Number)
  // eslint-disable-next-line no-restricted-syntax -- Date.UTC calendar arithmetic, no zone involved
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10)
}

function todayYmdEt() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date())
}

function rollupDailyToWeekly(daily) {
  const weeks = new Map()
  for (const row of daily) {
    const week = mondayOf(row.date)
    if (!weeks.has(week)) {
      weeks.set(week, { date: week, completed: 0, no_show: 0, cancelled: 0, in_progress: 0, other: 0 })
    }
    const agg = weeks.get(week)
    agg.completed += row.completed || 0
    agg.no_show += row.no_show || 0
    agg.cancelled += row.cancelled || 0
    agg.in_progress += row.in_progress || 0
    agg.other += row.other || 0
  }
  return [...weeks.values()]
}

function rateColor(rate) {
  if (rate > 30) return '#EF4444'
  if (rate > 15) return '#F59E0B'
  return '#22C55E'
}

function videoStackColor(stack) {
  if (stack === 'vidhealth') return '#6366F1'
  if (stack === 'zoom') return '#0EA5E9'
  if (stack === 'unset') return '#9CA3AF'
  return '#8B5CF6'
}

function punctualityColor(bucket) {
  if (bucket === 'early') return '#22C55E'
  if (bucket === 'on_time') return '#6366F1'
  if (bucket === 'late_5_15') return '#F59E0B'
  if (bucket === 'late_15') return '#EF4444'
  return '#9CA3AF'
}

function QualityStat({ label, value, warn = false }) {
  return (
    <div className="rounded-lg border p-3">
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      <div className={`text-lg font-semibold ${warn ? 'text-red-500' : ''}`}>{value}</div>
    </div>
  )
}

function exportCsv(filename, headers, rows) {
  const escape = (v) => {
    const s = v == null ? '' : String(v)
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`
    return s
  }
  const csv = [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

const TOOLTIP_STYLE = {
  backgroundColor: 'hsl(var(--popover))',
  color: 'hsl(var(--popover-foreground))',
  border: '1px solid hsl(var(--border))',
  borderRadius: '8px',
}

export default function AppointmentsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { currentClinicId } = useAuthStore()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState(null)
  const [volumeGranularity, setVolumeGranularity] = useState('auto')
  const [providerSort, setProviderSort] = useState({ key: 'total', dir: 'desc' })

  const dateRange = useMemo(() => {
    const startParam = searchParams.get('start')
    const endParam = searchParams.get('end')
    if (startParam && endParam) {
      const from = parse(startParam, 'yyyy-MM-dd', new Date())
      const to = parse(endParam, 'yyyy-MM-dd', new Date())
      if (!isNaN(from.getTime()) && !isNaN(to.getTime())) return { from, to }
    }
    return getDefaultDateRange()
  }, [searchParams])

  const setDateRange = useCallback((newRange) => {
    if (!newRange?.from || !newRange?.to) return
    setSearchParams(prev => {
      const params = new URLSearchParams(prev)
      params.set('start', format(newRange.from, 'yyyy-MM-dd'))
      params.set('end', format(newRange.to, 'yyyy-MM-dd'))
      return params
    })
  }, [setSearchParams])

  const fetchData = useCallback(async (refresh = false) => {
    if (!dateRange?.from || !dateRange?.to) return
    try {
      refresh || data ? setIsRefreshing(true) : setLoading(true)
      setError(null)
      const response = await appointmentsAPI.getReport({
        startDate: format(dateRange.from, 'yyyy-MM-dd'),
        endDate: format(dateRange.to, 'yyyy-MM-dd'),
      })
      if (response.data.success) setData(response.data.data)
    } catch (err) {
      console.error('[APPOINTMENTS] Error:', err)
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setLoading(false)
      setIsRefreshing(false)
    }
  }, [dateRange]) // eslint-disable-line react-hooks/exhaustive-deps -- `data` would retrigger on every load

  useEffect(() => {
    fetchData()
  }, [fetchData, currentClinicId])

  const summary = data?.summary
  const wow = data?.wow
  const tzLabel = data?.timezone === 'America/New_York' ? 'Eastern' : (data?.timezone || 'Eastern')

  const volumeSeries = useMemo(() => {
    const daily = data?.daily_volume || []
    const useWeekly = volumeGranularity === 'weekly'
      || (volumeGranularity === 'auto' && daily.length > 42)
    return useWeekly ? rollupDailyToWeekly(daily) : daily
  }, [data, volumeGranularity])

  const volumeIsWeekly = volumeGranularity === 'weekly'
    || (volumeGranularity === 'auto' && (data?.daily_volume?.length || 0) > 42)

  const sortedProviders = useMemo(() => {
    const rows = data?.by_provider || []
    const { key, dir } = providerSort
    return [...rows].sort((a, b) => {
      const av = a[key]
      const bv = b[key]
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      if (typeof av === 'string') return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av)
      return dir === 'asc' ? av - bv : bv - av
    })
  }, [data, providerSort])

  const toggleProviderSort = (key) => {
    setProviderSort(prev => prev.key === key
      ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
      : { key, dir: key === 'name' ? 'asc' : 'desc' })
  }

  const handleExportProviders = () => {
    const rows = sortedProviders.map(p => [
      p.name, p.email, p.total, p.completed, p.no_show, p.cancelled,
      p.no_show_rate, p.ft_no_show_rate, p.rpt_no_show_rate,
      p.capacity_slots || '', p.utilization ?? '',
    ])
    exportCsv(
      `appointments-providers-${format(dateRange.from, 'yyyy-MM-dd')}-to-${format(dateRange.to, 'yyyy-MM-dd')}.csv`,
      ['Provider', 'Email', 'Total', 'Completed', 'No-Show', 'Cancelled', 'NS Rate', 'First-Time NS', 'Repeat NS', 'Capacity', 'Utilization'],
      rows,
    )
  }

  const showDashboard = !error && (summary || loading)
  const initialLoad = loading && !data

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Appointments</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Telehealth volume, outcomes, and provider performance · days run on {tzLabel} time
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchData(true)}
            disabled={loading || isRefreshing}
          >
            {isRefreshing || loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="ml-1.5">Refresh</span>
          </Button>
        </div>
      </div>

      <div className="flex items-start gap-2 bg-blue-50 dark:bg-blue-950/30 border border-blue-300 dark:border-blue-700 rounded-lg p-3 text-sm text-foreground">
        <Info className="h-4 w-4 mt-0.5 shrink-0 text-blue-600 dark:text-blue-400" />
        <span>
          Scheduled dates and hours of day are {tzLabel}. No-show rate is no-shows ÷ (completed + no-shows).
          Source: telehealth appointment_v3.
        </span>
      </div>

      <p className="text-xs text-muted-foreground -mt-3">Filters by scheduled appointment date</p>

      <DataFreshnessWarning />

      {error && (
        <Card>
          <CardContent className="py-6">
            <div className="text-center">
              <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
              <p className="text-destructive">{error}</p>
              <Button variant="outline" onClick={() => fetchData(true)} className="mt-3">
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {initialLoad && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
              <p>Loading appointment data...</p>
            </div>
          </CardContent>
        </Card>
      )}

      {showDashboard && summary && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4">
            <KpiCard icon={Calendar} label="Total" value={formatNumber(summary.total)}>
              <PeriodDelta value={wow?.total_delta_pct} suffix="%" />
            </KpiCard>
            <KpiCard icon={CheckCircle2} iconClass="text-green-600" label="Completed" value={formatNumber(summary.completed)} valueClass="text-green-600">
              <p className="text-xs text-muted-foreground mt-0.5">{summary.completion_rate}% of closed</p>
            </KpiCard>
            <KpiCard icon={XCircle} iconClass="text-red-500" label="No-Shows" value={formatNumber(summary.no_show)} valueClass="text-red-500" />
            <KpiCard icon={XCircle} iconClass="text-gray-400" label="Cancelled" value={formatNumber(summary.cancelled)} valueClass="text-gray-500" />
            <KpiCard icon={Clock} label="Open" value={formatNumber(summary.pending ?? 0)}>
              <p className="text-xs text-muted-foreground mt-0.5">Booked, not started</p>
            </KpiCard>
            <KpiCard icon={TrendingUp} label="No-Show Rate" value={`${summary.no_show_rate}%`}>
              <PeriodDelta value={wow?.no_show_rate_delta} suffix="pp" invert />
            </KpiCard>
            <KpiCard icon={Clock} label="Avg Lead Time" value={`${summary.avg_lead_time_days}d`} />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <KpiCard icon={Video} iconClass="text-blue-500" label="In Progress" value={formatNumber(summary.in_progress ?? 0)}>
              <p className="text-xs text-muted-foreground mt-0.5">Patient already joined</p>
            </KpiCard>
            <KpiCard icon={Ban} iconClass="text-orange-500" label="Disqualified" value={formatNumber(summary.disqualified ?? 0)} valueClass="text-orange-500">
              <p className="text-xs text-muted-foreground mt-0.5">{summary.dq_rate ?? 0}% of completed</p>
            </KpiCard>
            <KpiCard icon={RefreshCw} label="Rescheduled" value={`${summary.reschedule_rate ?? 0}%`}>
              <p className="text-xs text-muted-foreground mt-0.5">{formatNumber(summary.rescheduled ?? 0)} visits moved</p>
            </KpiCard>
            <KpiCard icon={Video} iconClass="text-red-500" label="Zoom Join Fail" value={`${data?.video_join?.fail_rate ?? 0}%`}>
              <p className="text-xs text-muted-foreground mt-0.5">
                {formatNumber(data?.video_join?.failed_never_joined ?? 0)} of {formatNumber(data?.video_join?.zoom_visits ?? 0)} Zoom visits never connected
              </p>
            </KpiCard>
            <KpiCard icon={Disc3} label="Recording Ready" value={`${data?.recording?.coverage_rate ?? 0}%`}>
              <p className="text-xs text-muted-foreground mt-0.5">
                {formatNumber(data?.recording?.ready ?? 0)} of {formatNumber(data?.recording?.zoom_completed ?? 0)} Zoom completes
              </p>
            </KpiCard>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card>
              <CardContent className="py-4 px-4">
                <div className="text-xs text-muted-foreground mb-2">First-Time Patients</div>
                <p className="text-2xl font-bold">{summary.first_time?.no_show_rate ?? 0}%</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {formatNumber(summary.first_time?.no_show ?? 0)} no-shows of {formatNumber((summary.first_time?.completed ?? 0) + (summary.first_time?.no_show ?? 0))} resolved
                  {' '}· {formatNumber(summary.first_time?.total ?? 0)} booked
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4 px-4">
                <div className="text-xs text-muted-foreground mb-2">Repeat Patients</div>
                <p className="text-2xl font-bold">{summary.repeat?.no_show_rate ?? 0}%</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {formatNumber(summary.repeat?.no_show ?? 0)} no-shows of {formatNumber((summary.repeat?.completed ?? 0) + (summary.repeat?.no_show ?? 0))} resolved
                  {' '}· {formatNumber(summary.repeat?.total ?? 0)} booked
                </p>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {showDashboard && volumeSeries.length > 0 && (
        <Card>
          <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <CardTitle className="text-lg">
                {volumeIsWeekly ? 'Weekly' : 'Daily'} Appointment Volume
              </CardTitle>
              <CardDescription>
                {volumeIsWeekly
                  ? 'Stacked outcomes by Mon–Sun week. Auto-rolls up when the range is longer than six weeks.'
                  : `Appointments by status per day (${tzLabel})`}
              </CardDescription>
            </div>
            <div className="flex rounded-lg border border-input bg-background p-1 self-start">
              {[
                { value: 'auto', label: 'Auto' },
                { value: 'daily', label: 'Daily' },
                { value: 'weekly', label: 'Weekly' },
              ].map(g => (
                <button
                  key={g.value}
                  onClick={() => setVolumeGranularity(g.value)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    volumeGranularity === g.value
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={volumeSeries}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d) => {
                    const date = new Date(d + 'T00:00:00')
                    return `${date.getMonth() + 1}/${date.getDate()}`
                  }}
                  interval="preserveStartEnd"
                  fontSize={12}
                />
                <YAxis allowDecimals={false} />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  labelFormatter={(d) => {
                    const date = new Date(d + 'T00:00:00')
                    return volumeIsWeekly
                      ? `Week of ${format(date, 'MMM d, yyyy')}`
                      : format(date, 'MMM d, yyyy')
                  }}
                />
                <Legend />
                <Bar dataKey="completed" stackId="a" fill="#22C55E" name="Completed" />
                <Bar dataKey="no_show" stackId="a" fill="#EF4444" name="No-Show" />
                <Bar dataKey="cancelled" stackId="a" fill="#9CA3AF" name="Cancelled" />
                <Bar dataKey="in_progress" stackId="a" fill="#F59E0B" name="In progress" />
                <Bar dataKey="other" stackId="a" fill="#3B82F6" name="Open" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {showDashboard && data?.join_punctuality?.some(b => b.count > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Join Punctuality</CardTitle>
              <CardDescription>
                First non-reverted PATIENT_JOINED vs the scheduled start. Never joined is completed or no-show with no join event.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={data.join_punctuality}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="label" fontSize={12} interval={0} />
                  <YAxis allowDecimals={false} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Bar dataKey="count" name="Appointments" radius={[4, 4, 0, 0]}>
                    {data.join_punctuality.map((row) => (
                      <Cell key={row.bucket} fill={punctualityColor(row.bucket)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Zoom Visit Quality</CardTitle>
              <CardDescription>
                Connect and recording events written since the Zoom SDK rollout. Vidhealth visits have no VIDEO_* history by design.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <QualityStat label="Zoom visits" value={formatNumber(data.video_join?.zoom_visits)} />
                <QualityStat label="Connected" value={formatNumber(data.video_join?.joined)} />
                <QualityStat label="Failed, never joined" value={formatNumber(data.video_join?.failed_never_joined)} warn />
                <QualityStat label="Any join error" value={formatNumber(data.video_join?.failed)} />
                <QualityStat label="Recordings ready" value={formatNumber(data.recording?.ready)} />
                <QualityStat label="Recording failed" value={formatNumber(data.recording?.failed)} warn />
                <QualityStat label="Recording missing" value={formatNumber(data.recording?.missing)} />
                <QualityStat
                  label="Device-check fails"
                  value={data.device_check?.checked
                    ? `${data.device_check.fail_rate}% (${formatNumber(data.device_check.failed)}/${formatNumber(data.device_check.checked)})`
                    : '—'}
                  warn={data.device_check?.failed > 0}
                />
              </div>
              {data.device_check?.checked > 0 && (
                <p className="text-xs text-muted-foreground flex items-start gap-1.5">
                  <Smartphone className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  Device checks started mid-October on the Zoom waiting page. A fail is denied camera, a 0×0 frame, or Zoom video unsupported.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {showDashboard && data?.weekly_no_show_trend?.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">No-Show Rate — Week over Week</CardTitle>
            <CardDescription>
              Weekly no-show rate (Mon–Sun, {tzLabel}). Only resolved appointments (completed + no-show).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data.weekly_no_show_trend}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="week"
                  tickFormatter={(d) => {
                    const date = new Date(d + 'T00:00:00')
                    return `${date.getMonth() + 1}/${date.getDate()}`
                  }}
                  fontSize={12}
                />
                <YAxis tickFormatter={(v) => `${v}%`} />
                <Tooltip content={<RateTooltip labelPrefix="Week of " />} />
                <Line
                  type="monotone"
                  dataKey="no_show_rate"
                  stroke="#EF4444"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                  name="no_show_rate"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {showDashboard && data?.no_show_by_lead_time?.some(b => b.total > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">No-Show Rate by Lead Time</CardTitle>
              <CardDescription>
                Same-day bookings often no-show more. Rate over resolved appointments only.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RateBarChart
                data={data.no_show_by_lead_time.filter(b => b.total > 0)}
                xKey="bucket"
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Booking Lead Time</CardTitle>
              <CardDescription>
                How far in advance appointments are booked (all statuses)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={data.lead_time_distribution}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="bucket" fontSize={12} />
                  <YAxis allowDecimals={false} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Bar dataKey="count" fill="#6366F1" name="Appointments" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {showDashboard && (data?.no_show_by_hour?.length > 0 || data?.no_show_by_dow?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {data?.no_show_by_hour?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">No-Show Rate by Time of Day</CardTitle>
                <CardDescription>
                  Scheduled hour in {tzLabel}. Tooltip shows the sample size so a 50% of two appointments does not look like a pattern.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <RateBarChart data={data.no_show_by_hour} xKey="label" labelSuffix={` ${tzLabel}`} />
              </CardContent>
            </Card>
          )}
          {data?.no_show_by_dow?.some(d => d.total > 0) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">No-Show Rate by Day of Week</CardTitle>
                <CardDescription>
                  Resolved appointments, {tzLabel} weekday of the scheduled slot
                </CardDescription>
              </CardHeader>
              <CardContent>
                <RateBarChart data={data.no_show_by_dow} xKey="day" />
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {showDashboard && sortedProviders.length > 0 && (
        <Card>
          <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <CardTitle className="text-lg">Provider Performance</CardTitle>
              <CardDescription>
                Appointment outcomes and utilization by provider. Click a column to sort.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={handleExportProviders}>
              <Download className="h-4 w-4 mr-1.5" />
              CSV
            </Button>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <SortTh label="Provider" field="name" sort={providerSort} onSort={toggleProviderSort} align="left" />
                    <SortTh label="Total" field="total" sort={providerSort} onSort={toggleProviderSort} />
                    <SortTh label="Completed" field="completed" sort={providerSort} onSort={toggleProviderSort} />
                    <SortTh label="No-Show" field="no_show" sort={providerSort} onSort={toggleProviderSort} />
                    <SortTh label="Cancelled" field="cancelled" sort={providerSort} onSort={toggleProviderSort} />
                    <SortTh label="NS Rate" field="no_show_rate" sort={providerSort} onSort={toggleProviderSort} />
                    <SortTh label="1st-Time NS" field="ft_no_show_rate" sort={providerSort} onSort={toggleProviderSort} className="hidden md:table-cell" />
                    <SortTh label="Repeat NS" field="rpt_no_show_rate" sort={providerSort} onSort={toggleProviderSort} className="hidden md:table-cell" />
                    <SortTh label="Capacity" field="capacity_slots" sort={providerSort} onSort={toggleProviderSort} className="hidden lg:table-cell" />
                    <SortTh label="Util." field="utilization" sort={providerSort} onSort={toggleProviderSort} className="hidden lg:table-cell" />
                  </tr>
                </thead>
                <tbody>
                  {sortedProviders.map((row) => (
                    <tr key={row.email} className="border-t hover:bg-muted/30">
                      <td className="p-3">
                        <div className="font-medium text-sm">{row.name}</div>
                        <div className="text-xs text-muted-foreground">{row.email}</div>
                      </td>
                      <td className="p-3 text-right text-sm font-mono">{formatNumber(row.total)}</td>
                      <td className="p-3 text-right text-sm font-mono text-green-600">{formatNumber(row.completed)}</td>
                      <td className="p-3 text-right text-sm font-mono text-red-500">{formatNumber(row.no_show)}</td>
                      <td className="p-3 text-right text-sm font-mono text-gray-500">{formatNumber(row.cancelled)}</td>
                      <td className="p-3 text-right text-sm font-mono">
                        <NoShowRateCell rate={row.no_show_rate} count={row.no_show} total={row.completed + row.no_show} />
                      </td>
                      <td className="p-3 text-right text-sm font-mono hidden md:table-cell">
                        <NoShowRateCell rate={row.ft_no_show_rate} count={row.ft_no_show} total={(row.ft_completed ?? 0) + row.ft_no_show} />
                      </td>
                      <td className="p-3 text-right text-sm font-mono hidden md:table-cell">
                        <NoShowRateCell rate={row.rpt_no_show_rate} count={row.rpt_no_show} total={(row.rpt_completed ?? 0) + row.rpt_no_show} />
                      </td>
                      <td className="p-3 text-right text-sm font-mono hidden lg:table-cell">
                        {row.capacity_slots > 0 ? formatNumber(row.capacity_slots) : <span className="text-muted-foreground">-</span>}
                      </td>
                      <td className="p-3 text-right text-sm font-mono hidden lg:table-cell">
                        {row.utilization !== null ? (
                          <span className={row.utilization > 100 ? 'text-red-500' : row.utilization > 80 ? 'text-amber-500' : 'text-green-600'}>
                            {row.utilization}%
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {showDashboard && data?.by_video_stack?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Video Stack</CardTitle>
            <CardDescription>
              Share of appointments by video stack (Vidhealth vs Zoom). The provider is written at read/join time, so Not yet joined means the visit has not started — upcoming slots, and no-shows or cancels that never joined.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.by_video_stack}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="label" fontSize={12} />
                <YAxis tickFormatter={(v) => `${v}%`} />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(value, name, item) => {
                    if (name === 'share_pct') {
                      const row = item?.payload
                      return [`${value}% (${formatNumber(row?.total)} appts)`, 'Share']
                    }
                    return [value, name]
                  }}
                />
                <Bar dataKey="share_pct" name="share_pct" radius={[4, 4, 0, 0]}>
                  {data.by_video_stack.map((row) => (
                    <Cell key={row.stack} fill={videoStackColor(row.stack)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium">Stack</th>
                    <th className="text-right p-3 font-medium">Share</th>
                    <th className="text-right p-3 font-medium">Total</th>
                    <th className="text-right p-3 font-medium">Completed</th>
                    <th className="text-right p-3 font-medium">No-Show</th>
                    <th className="text-right p-3 font-medium">Cancelled</th>
                    <th className="text-right p-3 font-medium">Open</th>
                    <th className="text-right p-3 font-medium">NS Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_video_stack.map((row) => (
                    <tr key={row.stack} className="border-t hover:bg-muted/30">
                      <td className="p-3 font-medium text-sm">
                        <span className="inline-flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: videoStackColor(row.stack) }} />
                          {row.label}
                        </span>
                      </td>
                      <td className="p-3 text-right text-sm font-mono">{row.share_pct}%</td>
                      <td className="p-3 text-right text-sm font-mono">{formatNumber(row.total)}</td>
                      <td className="p-3 text-right text-sm font-mono text-green-600">{formatNumber(row.completed)}</td>
                      <td className="p-3 text-right text-sm font-mono text-red-500">{formatNumber(row.no_show)}</td>
                      <td className="p-3 text-right text-sm font-mono text-gray-500">{formatNumber(row.cancelled)}</td>
                      <td className="p-3 text-right text-sm font-mono">{formatNumber(row.pending)}</td>
                      <td className="p-3 text-right text-sm font-mono">
                        <NoShowRateCell rate={row.no_show_rate} count={row.no_show} total={(row.completed || 0) + (row.no_show || 0)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {showDashboard && (data?.by_source?.length > 0 || data?.by_timezone?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {data?.by_source?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">By Source</CardTitle>
                <CardDescription>Appointment outcomes by booking source</CardDescription>
              </CardHeader>
              <CardContent>
                <OutcomeTable
                  rows={data.by_source}
                  nameKey="source"
                  nameLabel="Source"
                />
              </CardContent>
            </Card>
          )}
          {data?.by_timezone?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">By Patient Timezone</CardTitle>
                <CardDescription>Outcomes grouped by the timezone on the patient record</CardDescription>
              </CardHeader>
              <CardContent>
                <OutcomeTable
                  rows={data.by_timezone}
                  nameKey="timezone"
                  nameLabel="Timezone"
                />
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {showDashboard && data?.consultation_duration?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Consultation Duration</CardTitle>
            <CardDescription>
              Actual time from patient joined to appointment completed, per provider
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium">Provider</th>
                    <th className="text-right p-3 font-medium">Completed</th>
                    <th className="text-right p-3 font-medium">Avg (min)</th>
                    <th className="text-right p-3 font-medium">Median (min)</th>
                    <th className="text-right p-3 font-medium hidden sm:table-cell">P90 (min)</th>
                  </tr>
                </thead>
                <tbody>
                  {data.consultation_duration.map((row) => (
                    <tr key={row.email} className="border-t hover:bg-muted/30">
                      <td className="p-3">
                        <div className="font-medium text-sm">{row.name}</div>
                        <div className="text-xs text-muted-foreground">{row.email}</div>
                      </td>
                      <td className="p-3 text-right text-sm font-mono">{formatNumber(row.count)}</td>
                      <td className="p-3 text-right text-sm font-mono">{row.avg_minutes}</td>
                      <td className="p-3 text-right text-sm font-mono">{row.median_minutes}</td>
                      <td className="p-3 text-right text-sm font-mono hidden sm:table-cell">{row.p90_minutes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {showDashboard && data?.double_booked?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Upcoming Double-Booked Patients</CardTitle>
            <CardDescription>
              Patients with 2+ appointments in the same week, next 30 days.{' '}
              {new Set(data.double_booked.map(d => d.patient_id)).size} unique patient{new Set(data.double_booked.map(d => d.patient_id)).size !== 1 ? 's' : ''},{' '}
              {data.double_booked.reduce((s, d) => s + d.appointments.length, 0)} overlapping appointments
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DoubleBookedCalendar doubleBooked={data.double_booked} />
          </CardContent>
        </Card>
      )}

      {showDashboard && !initialLoad && summary?.total === 0 && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <p>No appointments found for this date range.</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function KpiCard({ icon: Icon, iconClass = 'text-muted-foreground', label, value, valueClass = '', children }) {
  return (
    <Card>
      <CardContent className="py-4 px-4">
        <div className="flex items-center gap-2 mb-1">
          <Icon className={`h-4 w-4 ${iconClass}`} />
          <span className="text-xs text-muted-foreground">{label}</span>
        </div>
        <p className={`text-2xl font-bold ${valueClass}`}>{value}</p>
        {children}
      </CardContent>
    </Card>
  )
}

function RateBarChart({ data, xKey, labelSuffix = '' }) {
  return (
    <ResponsiveContainer width="100%" height={250}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
        <XAxis dataKey={xKey} fontSize={12} />
        <YAxis tickFormatter={(v) => `${v}%`} />
        <Tooltip content={<RateTooltip labelSuffix={labelSuffix} />} />
        <Bar dataKey="no_show_rate" name="no_show_rate" radius={[4, 4, 0, 0]}>
          {data.map((entry, i) => (
            <Cell key={`${entry[xKey]}-${i}`} fill={rateColor(entry.no_show_rate)} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}

function RateTooltip({ active, payload, label, labelPrefix = '', labelSuffix = '' }) {
  if (!active || !payload?.length) return null
  const row = payload[0].payload
  const total = row.total ?? ((row.completed || 0) + (row.no_show || 0))
  const dateLabel = row.week
    ? (() => {
        const date = new Date(row.week + 'T00:00:00')
        return `${labelPrefix}${format(date, 'MMM d, yyyy')}`
      })()
    : `${labelPrefix}${label}${labelSuffix}`
  return (
    <div className="bg-popover text-popover-foreground border border-border rounded-lg p-3 text-sm min-w-[160px]">
      <p className="font-medium mb-1">{dateLabel}</p>
      <p>No-show rate: {row.no_show_rate}%</p>
      <p className="text-xs text-muted-foreground mt-0.5">
        {formatNumber(row.no_show || 0)} of {formatNumber(total)} resolved
      </p>
    </div>
  )
}

function OutcomeTable({ rows, nameKey, nameLabel }) {
  return (
    <div className="border rounded-lg overflow-hidden">
      <table className="w-full">
        <thead className="bg-muted/50">
          <tr>
            <th className="text-left p-3 font-medium">{nameLabel}</th>
            <th className="text-right p-3 font-medium">Total</th>
            <th className="text-right p-3 font-medium">Completed</th>
            <th className="text-right p-3 font-medium">No-Show</th>
            <th className="text-right p-3 font-medium">Cancelled</th>
            <th className="text-right p-3 font-medium">NS Rate</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row[nameKey]} className="border-t hover:bg-muted/30">
              <td className="p-3 font-medium text-sm">{row[nameKey]}</td>
              <td className="p-3 text-right text-sm font-mono">{formatNumber(row.total)}</td>
              <td className="p-3 text-right text-sm font-mono text-green-600">{formatNumber(row.completed)}</td>
              <td className="p-3 text-right text-sm font-mono text-red-500">{formatNumber(row.no_show)}</td>
              <td className="p-3 text-right text-sm font-mono text-gray-500">{formatNumber(row.cancelled)}</td>
              <td className="p-3 text-right text-sm font-mono">
                <NoShowRateCell rate={row.no_show_rate} count={row.no_show} total={(row.completed || 0) + (row.no_show || 0)} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function SortTh({ label, field, sort, onSort, align = 'right', className = '' }) {
  const active = sort.key === field
  return (
    <th
      className={`${align === 'left' ? 'text-left' : 'text-right'} p-3 font-medium cursor-pointer select-none hover:text-foreground ${className}`}
      onClick={() => onSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown className={`h-3 w-3 ${active ? 'opacity-100' : 'opacity-40'}`} />
      </span>
    </th>
  )
}

function DoubleBookedCalendar({ doubleBooked }) {
  const dailyCounts = {}
  const dailyDetails = {}
  for (const row of doubleBooked) {
    for (const appt of row.appointments) {
      const day = appt.date?.slice(0, 10)
      if (!day) continue
      if (!dailyCounts[day]) { dailyCounts[day] = 0; dailyDetails[day] = [] }
      dailyCounts[day]++
      dailyDetails[day].push({ patient_id: row.patient_id, provider: appt.provider, status: appt.status })
    }
  }

  const start = todayYmdEt()
  const end = addDaysYmd(start, 30)
  const gridStart = mondayOf(start)
  let gridEnd = end
  const endDow = new Date(end + 'T00:00:00Z').getUTCDay()
  if (endDow !== 0) gridEnd = addDaysYmd(end, 7 - endDow)

  const weeks = []
  let cursor = gridStart
  while (cursor <= gridEnd) {
    const week = []
    for (let d = 0; d < 7; d++) {
      const inRange = cursor >= start && cursor <= end
      week.push({
        date: cursor,
        count: dailyCounts[cursor] || 0,
        inRange,
        details: dailyDetails[cursor] || [],
      })
      cursor = addDaysYmd(cursor, 1)
    }
    weeks.push(week)
  }

  const maxCount = Math.max(1, ...Object.values(dailyCounts))
  const [selectedDay, setSelectedDay] = useState(null)

  function getCellColor(count, inRange) {
    if (!inRange) return 'bg-muted/20'
    if (count === 0) return 'bg-muted/40'
    const intensity = count / maxCount
    if (intensity <= 0.25) return 'bg-amber-200 dark:bg-amber-900/50'
    if (intensity <= 0.5) return 'bg-amber-300 dark:bg-amber-800/60'
    if (intensity <= 0.75) return 'bg-orange-400 dark:bg-orange-700/70'
    return 'bg-red-500 dark:bg-red-700/80'
  }

  const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

  return (
    <div>
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full">
          <div className="flex gap-0.5 mb-0.5 ml-12">
            {dayLabels.map(d => (
              <div key={d} className="w-9 text-center text-[10px] text-muted-foreground font-medium">{d}</div>
            ))}
          </div>
          {weeks.map((week, wi) => {
            const weekLabel = new Date(week[0].date + 'T00:00:00')
            const monthLabel = wi === 0 || weekLabel.getDate() <= 7
              ? weekLabel.toLocaleString('en-US', { month: 'short' })
              : ''
            return (
              <div key={wi} className="flex gap-0.5 items-center">
                <div className="w-11 text-right text-[10px] text-muted-foreground pr-1 flex-shrink-0">
                  {monthLabel || <span>&nbsp;</span>}
                </div>
                {week.map((day) => (
                  <div
                    key={day.date}
                    className={`w-9 h-9 rounded-sm flex items-center justify-center text-xs font-mono relative
                      ${getCellColor(day.count, day.inRange)}
                      ${!day.inRange ? 'opacity-30' : ''}
                      ${day.count > 0 ? 'font-semibold' : 'text-muted-foreground/60'}
                    `}
                    title={day.count > 0 ? `${day.date}: ${day.count} double-booked appt${day.count !== 1 ? 's' : ''}` : day.date}
                    onClick={() => day.count > 0 && setSelectedDay(selectedDay?.date === day.date ? null : day)}
                    style={{ cursor: day.count > 0 ? 'pointer' : 'default' }}
                  >
                    {day.inRange ? Number(day.date.slice(8, 10)) : ''}
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
        <span>Less</span>
        <div className="w-4 h-4 rounded-sm bg-muted/40" />
        <div className="w-4 h-4 rounded-sm bg-amber-200 dark:bg-amber-900/50" />
        <div className="w-4 h-4 rounded-sm bg-amber-300 dark:bg-amber-800/60" />
        <div className="w-4 h-4 rounded-sm bg-orange-400 dark:bg-orange-700/70" />
        <div className="w-4 h-4 rounded-sm bg-red-500 dark:bg-red-700/80" />
        <span>More</span>
      </div>
      {selectedDay && (
        <div className="mt-3 p-3 border rounded-lg bg-muted/30 text-sm">
          <div className="font-medium mb-1">{selectedDay.date} — {selectedDay.count} double-booked appointment{selectedDay.count !== 1 ? 's' : ''}</div>
          <div className="space-y-0.5">
            {selectedDay.details.map((d, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="font-mono text-muted-foreground">{d.patient_id}</span>
                <span>→</span>
                <span>{d.provider}</span>
                <span className={`px-1 rounded ${
                  d.status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                  d.status === 'no_show' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                  d.status === 'cancelled' ? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' :
                  'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                }`}>{d.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function NoShowRateCell({ rate, count, total }) {
  if (total === 0 && count !== undefined) return <span className="text-muted-foreground">-</span>
  const colorClass = rate > 30 ? 'text-red-500' : rate > 15 ? 'text-amber-500' : 'text-green-600'
  return (
    <span className={colorClass} title={count !== undefined ? `${count} of ${total}` : undefined}>
      {rate}%
    </span>
  )
}

function PeriodDelta({ value, suffix = '', invert = false }) {
  if (value === null || value === undefined) return null
  const isPositive = value > 0
  const isGood = invert ? !isPositive : isPositive
  const colorClass = value === 0 ? 'text-muted-foreground' : isGood ? 'text-green-600' : 'text-red-500'
  const Icon = value > 0 ? TrendingUp : value < 0 ? TrendingDown : null
  return (
    <p className={`text-xs mt-0.5 flex items-center gap-0.5 ${colorClass}`}>
      {Icon && <Icon className="h-3 w-3" />}
      {Math.abs(value)}{suffix} vs prior period
    </p>
  )
}
